import type { TFunction } from 'i18next';

import type { HouseholdMemberItem } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';

export const slug = (input: string) => {
  let output = input
    .replaceAll(' / ', '_')
    .replaceAll(' - ', '_')
    .replaceAll(' & ', '_')
    .replaceAll(', ', '_')
    .replaceAll('-', '_')
    .replaceAll(' ', '_');
  if (output.length >= 1) {
    output = output
      .split('_')
      .map((word) => word[0].toUpperCase() + word.substring(1).toLowerCase())
      .join('');
  }
  return output;
};

export const key = (prefix: string, input: string) => {
  let output = slug(input);
  if (output.length >= 1) {
    if (
      prefix.substring(prefix.length - 1) === '.' ||
      prefix.substring(prefix.length - 1) === ':'
    ) {
      output = output[0].toLowerCase() + output.substring(1);
    }
  }
  return prefix.concat(output);
};

export const templateString = (prefix: string, input: string) =>
  key(prefix, input) as unknown as TemplateStringsArray;

export const religousCapitalization = (input: string) =>
  input
    .toLowerCase()
    .replace('bible', 'Bible')
    .replace('god', 'God')
    .replace('dios', 'Dios');

export const getFamilyList = (
  t: TFunction,
  householdMemberList: HouseholdMemberItem[] | null | undefined,
) => {
  const familyArray: string[] = [];
  let sisterCount = 0;
  let brotherCount = 0;

  if (householdMemberList) {
    householdMemberList.forEach((relative) => {
      if (relative.householdMemberRole === 'Sister') {
        sisterCount += 1;
      } else if (relative.householdMemberRole === 'Brother') {
        brotherCount += 1;
      } else if (
        relative.householdMemberRole &&
        relative.householdMemberRole !== 'Beneficiary - Female' &&
        relative.householdMemberRole !== 'Beneficiary - Male' &&
        relative.householdMemberRole !== 'Participant - Female' &&
        relative.householdMemberRole !== 'Participant - Male'
      ) {
        familyArray.push(
          t(templateString('bio:', relative.householdMemberRole)),
        );
      }
    });
  }

  // @TODO: review i18next singular/plural rules with translator. Default KO rules do not match the current Compassion Korea website
  if (sisterCount === 1) {
    familyArray.push(t('bio:sister'));
  }

  if (sisterCount > 1) {
    familyArray.push(t('bio:sisters', { number: sisterCount }));
  }

  if (brotherCount === 1) {
    familyArray.push(t('bio:brother'));
  }

  if (brotherCount > 1) {
    familyArray.push(t('bio:brothers', { number: brotherCount }));
  }

  if (familyArray.length === 0) {
    return t('bio:familySize', {
      context: 'zero',
      firstRelative: t('bio:family'),
    }).toString();
  }

  if (familyArray.length === 1) {
    return t('bio:familySize', {
      context: 'one',
      firstRelative: familyArray[0],
    }).toString();
  }

  if (familyArray.length === 2) {
    return t('bio:familySize', {
      context: 'two',
      firstRelative: familyArray[0],
      lastRelative: familyArray[1],
    }).toString();
  }

  if (familyArray.length >= 3) {
    return t('bio:familySize', {
      context: 'other',
      firstRelative: familyArray[0],
      additionalRelatives: familyArray
        .slice(1, familyArray.length - 1)
        .join(', '),
      lastRelative: familyArray[familyArray.length - 1],
    }).toString();
  }

  return '';
};

export const listFormat = (t: TFunction, items?: string[]) => {
  if (t('bio:localeFormat') === 'ko') {
    return items?.map((item) => t(templateString('bio:', item)))?.join(', ');
  }

  const formatter = new Intl.ListFormat(t('bio:localeFormat'), {
    style: 'long',
    type: 'conjunction',
  });
  return formatter.format(
    items?.map((item) =>
      religousCapitalization(t(templateString('bio:', item))),
    ) as Iterable<string>,
  );
};
